import type { AddCartItemInput, CartItem, CartOperation } from "../types/cart.types";
import {
    clampCartQuantity,
    getCartItemQuantityContext,
    normalizeCartItemMetadata,
} from "../utils/cartQuantity";

const isSameLine = (
    item: CartItem,
    identity: Pick<CartItem, "idProduct" | "idProductVariant">,
): boolean =>
    item.idProduct === identity.idProduct && item.idProductVariant === identity.idProductVariant;

const addItem = (items: CartItem[], item: AddCartItemInput): CartItem[] => {
    const existingItem = items.find((cartItem) => isSameLine(cartItem, item));
    const hasIncomingMetadata =
        item.unitMeasureCode !== undefined ||
        item.unitLabel !== undefined ||
        item.cantDecimal !== undefined ||
        item.minimumQuantity !== undefined;
    const mergedItem = normalizeCartItemMetadata({
        ...(existingItem ?? {}),
        ...item,
        stock:
            existingItem && !hasIncomingMetadata && item.stock === undefined
                ? existingItem.stock
                : item.stock,
        unitMeasureCode: item.unitMeasureCode ?? existingItem?.unitMeasureCode,
        unitLabel: item.unitLabel ?? existingItem?.unitLabel,
        cantDecimal: item.cantDecimal ?? existingItem?.cantDecimal,
        minimumQuantity: item.minimumQuantity ?? existingItem?.minimumQuantity,
    } as AddCartItemInput);
    const context = getCartItemQuantityContext(mergedItem);
    if (context.maximumQuantity < context.minimumQuantity) return items;

    if (!existingItem) {
        const quantity = clampCartQuantity(item.quantity ?? 1, context);
        if (quantity == null || quantity <= 0) return items;

        return [
            {
                ...mergedItem,
                quantity,
                attributes: item.attributes || [],
            },
            ...items,
        ];
    }

    const quantityToAdd = clampCartQuantity(item.quantity ?? 1, context);
    if (quantityToAdd == null || quantityToAdd <= 0) return items;

    const nextQuantity = clampCartQuantity(existingItem.quantity + quantityToAdd, context);
    if (nextQuantity == null || nextQuantity <= 0) return items;

    return items.map((cartItem) =>
        isSameLine(cartItem, item)
            ? {
                  ...cartItem,
                  ...mergedItem,
                  quantity: nextQuantity,
                  attributes: item.attributes || cartItem.attributes || [],
              }
            : cartItem,
    );
};

const removeItem = (
    items: CartItem[],
    idProduct: string,
    idProductVariant?: string,
): CartItem[] => {
    const nextItems = items.filter(
        (item) => !(item.idProduct === idProduct && item.idProductVariant === idProductVariant),
    );

    return nextItems.length === items.length ? items : nextItems;
};

const updateItemQuantity = (
    items: CartItem[],
    idProduct: string,
    idProductVariant: string | undefined,
    quantity: number,
): CartItem[] => {
    let changed = false;
    const nextItems = items.map((item) => {
        if (item.idProduct !== idProduct || item.idProductVariant !== idProductVariant) {
            return item;
        }

        const context = getCartItemQuantityContext(item);
        if (context.maximumQuantity < context.minimumQuantity) return item;

        const safeQuantity = clampCartQuantity(quantity, context);
        if (safeQuantity == null || safeQuantity <= 0) return item;
        if (safeQuantity === item.quantity) return item;

        changed = true;
        return {
            ...normalizeCartItemMetadata(item),
            quantity: safeQuantity,
        };
    });

    return changed ? nextItems : items;
};

export const applyCartOperations = (
    currentItems: CartItem[],
    operations: CartOperation[],
): CartItem[] =>
    operations.reduce<CartItem[]>((items, operation) => {
        switch (operation.type) {
            case "add":
                return addItem(items, operation.item);
            case "remove":
                return removeItem(items, operation.idProduct, operation.idProductVariant);
            case "updateQuantity":
                return updateItemQuantity(
                    items,
                    operation.idProduct,
                    operation.idProductVariant,
                    operation.quantity,
                );
            case "clear":
                return items.length === 0 ? items : [];
            default:
                return items;
        }
    }, currentItems);
